/**
 * La franja que dice con quién estás hablando (GKTIBO-124).
 *
 * ## Por qué existe
 *
 * El paso 2 usa **una sola caja de conversación** para dos interlocutores:
 * primero Tibo entrevista al dueño, y cuando termina, esa misma caja pasa a
 * ser su bot y él lo prueba. Es lo que hace que el paso se sienta uno solo en
 * vez de un salto de pantalla — que era la queja concreta del PO: *«parece que
 * se saltara de un lado al otro»*.
 *
 * El riesgo de reusar la caja es que no se sepa a quién se le escribe. El
 * dueño podría corregir a Tibo creyendo que corrige a su bot, y no habría
 * forma de que se diera cuenta.
 *
 * ## Por qué con texto y no sólo con color
 *
 * El color ayuda a notar el cambio de reojo, y por eso las dos franjas se ven
 * distintas. Pero el color **no se lee**: ni con un lector de pantalla ni por
 * alguien que no distingue ese par de tonos. Lo que dice quién habla es la
 * frase.
 */

import { Bot, MessageSquare } from 'lucide-react';
import { entrevista } from './textos-entrevista';
import { probar } from './textos-probar';

export interface ConQuienHablasProps {
  quien: 'tibo' | 'bot';
  /** El negocio del dueño. Es la diferencia entre «un bot» y «el mío». */
  negocio: string;
  /** Si el dueño le puso nombre. Sin nombre no se inventa ninguno. */
  nombreDelBot?: string;
  /** En la entrevista: en cuál de las preguntas va. */
  cuenta?: string;
}

export function ConQuienHablas({ quien, negocio, nombreDelBot, cuenta }: ConQuienHablasProps) {
  const esTibo = quien === 'tibo';

  const titulo = esTibo
    ? entrevista.chat.hablaTibo
    : nombreDelBot
      ? probar.hablasConTuBotConNombre(nombreDelBot)
      : probar.hablasConTuBot;

  const bajada = esTibo ? entrevista.chat.hablaTiboBajada : probar.comoUnClienteDe(negocio);

  return (
    <div
      className={`con-quien-hablas${esTibo ? ' es-tibo' : ' es-bot'}`}
      data-testid="con-quien-hablas"
    >
      <span className="con-quien-avatar" aria-hidden="true">
        {esTibo ? <MessageSquare size={16} /> : <Bot size={16} />}
      </span>

      <span className="con-quien-texto">
        <strong className="con-quien-titulo">{titulo}</strong>
        <span className="con-quien-bajada">{bajada}</span>
      </span>

      {/* Sólo en la prueba: es lo que hace que el dueño se anime a escribirle
          cualquier cosa. En la entrevista sería mentira — ahí SÍ se escribe el
          conocimiento de su bot. */}
      {!esTibo && <span className="con-quien-sello">{probar.noTocaTuNegocio}</span>}

      {esTibo && cuenta && <span className="con-quien-cuenta">{cuenta}</span>}
    </div>
  );
}
